<template>
  <div class="licence-association-modal tw-p-5 leading-tight">
    <swiper
      :allowTouchMove="false"
      @init="onInit"
      @swiper="onSwiper"
      @slideChangeTransitionEnd="onSlideChangeTransitionEnd"
      @slideChangeTransitionStart="onSlideChangeTransitionStart"
    >
      <!-- Slide 1: Formulaire -->
      <swiper-slide>
        <LoadingMini ref="loading"></LoadingMini>
        <div class="messages" style="margin-top: 0; position: static;">
          <div class="message error show" v-if="!!backendErrorMessages.length">
            <i class="fa fa-times" @click="removeErrorsMessages()"></i>
            <i class="fa fa-exclamation-circle indicator"></i>
            <div class="message-inner">
              <div v-for="message in backendErrorMessages" v-text="message"></div>
            </div>
          </div>
        </div>
        
        <div class="form-row row standard">
          <label class="col-12">
            {{ $t("device.registrations.licence_number") }}
          </label>
          <input 
            type="text" 
            :value="formatLicenceId(props.dati.id)" 
            disabled 
            class="form-input col-12 text-mono"
          />
        </div>

        <div class="form-row row standard">
          <label class="col-12">
            {{ $t('device.registrations.phonenumber') }}
          </label>
          <FormInputPhone
            v-model="phoneNumber"
            name="phoneNumber"
            @input="onPhoneInput"
            :isInvalid="phoneNumberShowError && phoneNumberIsInvalid"
            @blur="blurPhoneInput"
          />
          <FormError v-if="phoneNumberShowError && phoneNumberIsInvalid">
            {{ $t('device.registrations.errors.invalidPhoneFormat') }}
          </FormError>
        </div>

        <div class="form-row row standard">
          <label class="col-12">
            {{ $t('device.registrations.association_type.email') }}
          </label>
          <input 
            type="email" 
            v-model="email" 
            class="form-input col-12"
            @blur="blurEmailInput"
          />
          <FormError v-if="emailShowError && emailIsInvalid">
            {{ $t('device.registrations.errors.invalidEmailFormat') }}
          </FormError>
        </div>

        <div class="form-row row standard">
          <label class="col-12">
            {{ $t('device.registrations.datiName') }}
          </label>
          <input 
            ref="datiNameInput" 
            type="text" 
            v-model="datiName" 
            class="form-input col-12"
            @input="onDatiNameInput"
          />
          <FormError v-if="datiNameIsInvalid">
            {{ $t('device.registrations.errors.label_needed') }}
          </FormError>
        </div>

        <div class="form-row row standard">
          <label class="col-12">
            {{ $t('device.registrations.profile') }}
          </label>
          <select 
            class="form-input col-12" 
            v-model="datiProfile"
            @input="onProfilInput"
          >
            <option :value="null">-- {{ $t('device.registrations.profileSelectPlaceholder') }} --</option>
            <option v-for="profile in props.profiles" :key="profile.profileId" :value="profile.profileId">
              {{ profile.meta.name }}
              {{ profile.meta.default ? '★' : null }}
            </option>
          </select>
          <FormError v-if="datiProfileIsInvalid">
            {{ $t('device.registrations.errors.profile_needed') }}
          </FormError>
        </div>

        <div class="form-row row standard" v-show="useDvFlagAccess('beepiz-group-can:update')">
          <label class="col-12">
            {{ $t('device.groupSelect') }} ({{ $t('global.optional') }})
          </label>
          <select class="form-input col-12" v-model="datiGroup">
            <option :value="null" class="text-muted">-- {{ $t('device.noGroup') }} --</option>
            <option v-for="group in props.groups" :key="group.id" :value="group.id">
              {{ group.name }}
            </option>
          </select>
        </div>

        <div class="form-row row standard" v-if="hasAnalytic">
          <label class="col-12">
            {{ $t('device.registrations.analyticCode') }} ({{ $t('global.optional') }})
          </label>
          <input 
            ref="analytic" 
            type="text" 
            maxlength="30" 
            v-model="analyticCode" 
            class="form-input col-12"
          />
        </div>

        <div class="d-flex py-2">
          <Btn 
            class="secondary ml-auto"
            :class="{ disabled: !formIsValid }"
            @click.native.stop="nextSlide"
          >
            <span>{{ $t('global.next') }}</span> <i class="fa fa-chevron-right"></i>
          </Btn>
        </div>
      </swiper-slide>

      <!-- Slide 2: Sélection du type -->
      <swiper-slide>
        <p class="explaination-2 mt-0 mb-2">
          {{ $t('device.registrations.association_type.explanation') }}
        </p>
        
        <div class="type-selection-container">
          <!-- SMS Button -->
          <Button2
            class="type-button"
            :class="{ disabled: !canSendSms || isLoading }"
            :disabled="!canSendSms || isLoading"
            @click.native.stop="selectType('sms')"
          >
            <div class="association_type">
                <i class="fa fa-mobile fa-2x"></i>
                <span class="type-label">{{ $t('device.registrations.association_type.sms') }}</span>
            </div>
            <span v-if="!canSendSms" class="type-hint">
              {{ $t('device.registrations.association_type.requires_phone') }}
            </span>
          </Button2>
          
          <!-- Email Button -->
          <Button2
            class="type-button"
            :class="{ disabled: !canSendEmail || isLoading }"
            :disabled="!canSendEmail || isLoading"
            @click.native.stop="selectType('email')"
          >
            <div class="association_type">
                <i class="fa fa-envelope fa-2x"></i>
                <span class="type-label">{{ $t('device.registrations.association_type.email') }}</span>
            </div>
            <span v-if="!canSendEmail" class="type-hint">
              {{ $t('device.registrations.association_type.requires_email') }}
            </span>
          </Button2>
          
          <!-- QR Code Button -->
          <Button2
            class="type-button"
            :class="{ disabled: !canShowQrCode || isLoading }"
            :disabled="!canShowQrCode || isLoading"
            @click.native.stop="selectType('qrcode')"
          >
            <div class="association_type">
              <i class="fa fa-qrcode fa-2x"></i>
              <span class="type-label">{{ $t('device.registrations.association_type.qrcode') }}</span>
            </div>
          </Button2>
        </div>
        
        <div class="d-flex py-2">
          <Btn 
            class="secondary prev-button"
            :disabled="isLoading" 
            @click.native.stop="prevSlide"
          >
            <i class="fa fa-chevron-left"></i> <span>{{ $t('global.prev') }}</span>
          </Btn>
        </div>
      </swiper-slide>

      <!-- Slide 3: QRCode Display -->
      <swiper-slide>
        <p class="explaination-2 mt-0 mb-2"></p>
        <div class="qrcode-container">
          <div :class="'qrcode-inner'">
            <LoadingMini ref="loadingQr"></LoadingMini>
            <QRCode 
              :text="qrCodeUrl" 
              :size="150" 
              :color="'#212121'" 
              :class="{show: qrCodeUrl != ''}"
              :bg-color="'#FFFFFF'" 
              :error-level="'Q'"
            ></QRCode>
          </div>
          <p class="mt-0 mb-1">
            <a :href="qrCodeUrl" target="_blank">{{ qrCodeUrl }}</a>
          </p>
          <span v-if="qrCodeUrl != ''" class="text-info-600">
            <span>{{ $t("device.registrations.expiresIn") }}</span>
            <Countdown :date="qrCodeExpiration" @expired="handleQrCodeExpired"></Countdown>
          </span>
        </div>

        <p
          class="explaination-3 my-2"
          v-html="$t('device.registrations.assocExplaination3')"
        ></p>
        <div class="center">
          <Btn class="primary reverse">
            <router-link :to="deviceLink" tag="div">
              <i class="fa fa-mobile-phone"></i>
              <span>{{ $t('device.btns.goToShow') }}</span>
            </router-link>
          </Btn>
        </div>
      </swiper-slide>
    </swiper>
  </div>
</template>

<script>
import axios from 'axios';
import API from '@/services/API';
import Utils from '@/services/utils';
import { Swiper, SwiperSlide } from 'swiper-vue2';
import Btn from '@/components/ui/Button';
import Button2 from "@/components/ui/Button2.vue";
import QRCode from 'vue-qrcode-component';
import LoadingMini from '@/components/ui/LoadingMini';
import Countdown from '@/components/ui/Countdown';
import { FormInputPhone, FormError } from '@/components/ui/forms';
import { useDvFlagAccess } from '../../services/featureFlag';

const { formatLicenceId } = Utils;

export default {
  name: 'LicenceAssociationModal',
  
  components: {
    Swiper,
    SwiperSlide,
    Btn,
    Button2,
    QRCode,
    LoadingMini,
    Countdown,
    FormInputPhone,
    FormError
  },
  
  props: {
    props: {
      type: Object,
      default: function () {
        return {
          dati: null,
          availableLicences: {},
          selectedLicenceType: null,
          profiles: [],
          groups: []
        }
      }
    }
  },
  
  data() {
    return {
      swiperInstance: null,
      currentSlide: 0,
      // Form data
      datiName: '',
      datiNameIsInvalid: false,
      phoneNumber: '',
      phoneNumberNational: null,
      phoneNumberIsInvalid: false,
      phoneNumberShowError: false,
      email: '',
      emailShowError: false,
      datiProfile: null,
      datiProfileIsInvalid: false,
      datiGroup: null,
      analyticCode: '',
      // Type selection
      selectedType: null, // 'sms', 'email', 'qrcode'
      // QRCode data
      qrCodeUrl: '',
      qrCodeExpiration: null,
      datiId: null,
      // API state
      isLoading: false,
      backendErrorMessages: []
    };
  },
  
  computed: {
    /**
     * Check if analytic code feature is enabled
     */
    hasAnalytic(){
        return window.V.clientInfos.beepiz_analytic_code
    },
    
    /**
     * Validate email format
     */
    emailIsInvalid() {
      if (!this.email || this.email.trim() === '') {
        return false; // Empty is valid (optional field)
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !emailRegex.test(this.email);
    },
    
    /**
     * Check if form is valid (required fields filled)
     */
    formIsValid() {
      const hasName = this.datiName && this.datiName.trim() !== '';
      const phoneValid = !this.phoneNumberIsInvalid || this.phoneNumberNational === undefined;
      const emailValid = !this.emailIsInvalid;
      
      return hasName && phoneValid && emailValid;
    },
    
    /**
     * Check if SMS can be sent (phone number valid)
     */
    canSendSms() {
      return this.phoneNumber && !this.phoneNumberIsInvalid;
    },
    
    /**
     * Check if Email can be sent (email valid)
     */
    canSendEmail() {
      return this.email && !this.emailIsInvalid;
    },
    
    /**
     * Check if QR Code can be shown (always available)
     */
    canShowQrCode() {
      return true;
    },
    
    /**
     * Generate device link for navigation
     */
    deviceLink() {
      if (this.datiId) {
        return `/devices/${this.datiId}?fromRegistration=1`;
      }
      return '#';
    }
  },
  
  methods: {
    useDvFlagAccess,
    formatLicenceId,
    
    /**
     * Handle phone input validation
     */
    onPhoneInput(value, objectValue) {
      this.phoneNumberNational = objectValue.nationalNumber;
      this.phoneNumberIsInvalid = !objectValue.valid;
    },
    
    /**
     * Handle dati name input validation
     */
    onDatiNameInput(value) {
      this.datiNameIsInvalid = !value;
    },
    
    /**
     * Handle profile input validation
     */
    onProfilInput(value) {
      this.datiProfileIsInvalid = !value;
    },
    
    /**
     * Show phone validation error on blur
     */
    blurPhoneInput() {
      this.phoneNumberShowError = this.phoneNumberNational !== undefined &&
                                   this.phoneNumberNational.length > 0;
    },
    
    /**
     * Show email validation error on blur
     */
    blurEmailInput() {
      this.emailShowError = this.email && this.email.trim() !== '';
    },
    
    /**
     * Remove error messages
     */
    removeErrorsMessages() {
      this.backendErrorMessages = [];
    },
    
    /**
     * Initialisation du Swiper
     */
    onInit(swiper) {
      this.$refs.loading.show();
      
      // If dati already has a registration, go to slide 2
      if (this.props.dati != null && this.props.dati.status == 1) {
        this.qrCodeUrl = this.props.dati.registration.shortLink;
        this.datiId = this.props.dati.registration.datiId;
        this.qrCodeExpiration = this.props.dati.registration.liveUntil;
        swiper.slideTo(2, 0, false);
      }
      if (this.props.dati != null && this.props.dati.status == 0) {
        swiper.slideTo(0, 0, false);
      }
      
      this.$refs.loading.hide();
    },
    
    /**
     * Récupération de l'instance Swiper
     */
    onSwiper(swiperRef) {
      this.swiperInstance = swiperRef;
    },
    
    /**
     * Callback après changement de slide
     */
    onSlideChangeTransitionEnd(swiper) {
      this.currentSlide = swiper.activeIndex;
    },

    /**
     * Callback avant changement de slide
     */
    onSlideChangeTransitionStart(swiper) {
      window.Vfc.$refs.modal.$el.lastChild.scrollTo({
        top: 0,
      });
    },
    
    /**
     * Navigation vers le slide suivant
     */
    nextSlide() {
      if (this.swiperInstance && this.formIsValid) {
        this.backendErrorMessages = [];
        this.swiperInstance.slideNext();
      }
    },
    
    /**
     * Navigation vers le slide précédent
     */
    prevSlide() {
      if (this.swiperInstance) {
        this.swiperInstance.slidePrev();
      }
    },
    
    /**
     * Handle type selection (SMS, Email, QRCode)
     * @param {string} type - Type selected: 'sms', 'email', or 'qrcode'
     */
    async selectType(type) {
      this.selectedType = type;
      this.isLoading = true;
      this.backendErrorMessages = [];
      
      // Show loading indicator
      if (this.$refs.loading) {
        this.$refs.loading.show();
      }

      try {
        const payload = this.buildPayload(type);
        const response = await axios.post(API.endpoints.licences.add, payload);

        if (type === 'qrcode') {
          // Extract QRCode data from response
          if (response.data.registration != null) {
            this.qrCodeUrl = response.data.registration.shortLink;
            this.datiId = response.data.registration.datiId;
            this.qrCodeExpiration = response.data.registration.liveUntil;
          } else {
            this.qrCodeUrl = response.data.message.shortLink;
            this.datiId = response.data.message.datiId;
            this.qrCodeExpiration = response.data.message.liveUntil;
          }
          this.swiperInstance.slideNext(); // Navigate to Slide 3
        } else {
          // SMS or Email: close modal
          window.Vfc.$refs.modal.hide();
        }
      } catch (error) {
        console.log(error)
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
        if (this.$refs.loading) {
          this.$refs.loading.hide();
        }
      }
    },
    
    /**
     * Build payload according to selected type
     * @param {string} type - Type: 'sms', 'email', or 'qrcode'
     * @returns {Object} Payload for API call
     */
    buildPayload(type) {
      const basePayload = {
        datiId: this.props.dati.id || null,
        datiName: [this.datiName],
        offer: this.props.selectedLicenceType,
        profileId: this.datiProfile,
        groupId: this.datiGroup || null,
        analyticCode: this.hasAnalytic ? [this.analyticCode] : [],
        type_registration: type
      };

      if (this.phoneNumber && !this.phoneNumberIsInvalid) {
        basePayload.recipients = [Utils.phoneCleaner(this.phoneNumber)];
      }
      if (this.email && !this.emailIsInvalid) {
          basePayload.email_recipient = this.email;
        }

      return basePayload;
    },
    
    /**
     * Handle API errors
     * @param {Error} error - Error object from axios
     */
    handleApiError(error) {
      if (error.response && error.response.data) {
        // Check for specific error message
        if (error.response.data.message === "phone_number_already_in_use") {
          this.backendErrorMessages = [window.V.$i18n.t('device.registrations.errors.phone_number_already_in_use')];
          this.prevSlide(); // Go back to form
        } else {
          const errors = error.response.data.errors || {};
          this.backendErrorMessages = Object.values(errors).flat();
          
          // If no structured errors, use the message field
          if (this.backendErrorMessages.length === 0 && error.response.data.message) {
            this.backendErrorMessages = [error.response.data.message];
          }
        }
      } else {
        window.Vfc.$refs.messages.sendMessage('error', window.V.$i18n.t('errors.default'));
        window.Vfc.$refs.modal.hide()
      }
    },
    
    /**
     * Handle QRCode expiration
     */
    handleQrCodeExpired() {
      this.qrCodeUrl = '';
    },
    
    /**
     * Close the modal
     */
    closeModal() {
      window.Vfc.$refs.modal.hide();
    }
  },
  
  mounted() {
    // Initialize form with existing dati data if available
    if (this.props.dati != null) {
      this.datiName = this.props.dati.name || '';
      this.analyticCode = this.props.dati.analyticCode || '';
      if (this.props.dati.profileId != null) {
        this.datiProfile = this.props.dati.profileId;
      }
      if (this.props.dati.groupId != null) {
        this.datiGroup = this.props.dati.groupId;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.qrcode-container {
  .qrcode-inner {
    canvas {
      opacity: 0;
      transition: opacity 0.3s ease;
      
      &.show {
        opacity: 1;
      }
    }
  }
}

.type-selection-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 2rem 0;
  
  .type-button {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 1.5rem;
    min-height: 120px;
    border: 2px solid #dee2e6;
    border-radius: 8px;
    background-color: var(--colors-white);
    transition: all 0.3s ease;
    cursor: pointer;

    .association_type {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
    }
    
    i {
      color: var(--colors-brand);
    }
    
    .type-label {
      font-size: 1.125rem;
      font-weight: 600;
      margin-left: 0.5rem;
    }
    
    .type-hint {
      font-size: 0.875rem;
      color: #6c757d;
      font-style: italic;
    }
    
    &:hover:not(.disabled) {
      border-color: var(--colors-brand);
      background-color: #f8f9fa;
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      background-color: #f8f9fa;
      
      i {
        color: #6c757d;
      }
      
      &:hover {
        transform: none;
        box-shadow: none;
        border-color: #dee2e6;
      }
    }
  }
}
</style>
